import { supabase } from '../supabase';
import { getMetaAdsToken } from '../platformAuth';
import { toast } from 'react-hot-toast';

// Helper function to validate dates
function isValidDate(date: Date | string | number): boolean {
  const d = new Date(date);
  return d instanceof Date && !isNaN(d.getTime());
}

// Helper function to safely create ISO string
function safeISOString(date: Date | string | number): string {
  if (!isValidDate(date)) {
    console.error('Invalid date:', date);
    return new Date().toISOString(); // Fallback to current time
  }
  return new Date(date).toISOString();
}

// Helper function to format date for API
function formatDateForAPI(date: Date): string {
  return date.toISOString().split('T')[0];
}

interface MetaAdsError {
  error: {
    message: string;
    type: string;
    code: number;
    error_subcode?: number;
  };
}

interface MetaAdAccount {
  id: string;
  account_id: string;
  name: string;
  account_status: number;
  business_name?: string;
  currency: string;
  timezone_name: string;
}

interface MetaAdsInsights {
  spend: string;
  impressions: string;
  clicks: string;
  ctr: string;
  reach: string;
}

export async function fetchMetaAdsData() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      throw new Error('User not authenticated');
    }

    let retryCount = 0;
    const maxRetries = 3;
    let accessToken = null;

    while (retryCount < maxRetries && !accessToken) {
      accessToken = await getMetaAdsToken(session.user.id);
      if (!accessToken) {
        retryCount++;
        if (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
      }
    }

    if (!accessToken) {
      throw new Error('Failed to get valid access token. Please reconnect your Meta Ads account.');
    }

    const adAccounts = await fetchAdAccounts(accessToken);
    console.log('Fetched ad accounts:', adAccounts);

    const activeAccount = adAccounts.find(account => account.account_status === 1);
    if (!activeAccount) {
      throw new Error('No active Meta Ads accounts found. Please make sure you have an active ads account.');
    }

    // Ensure valid dates for the API request
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Simplified insights request with essential metrics
    const insightsResponse = await fetchWithRetry(
      `https://graph.facebook.com/v18.0/act_${activeAccount.account_id}/insights?` +
      `fields=spend,impressions,clicks,ctr,reach&` +
      `time_range={'since':'${formatDateForAPI(thirtyDaysAgo)}','until':'${formatDateForAPI(today)}'}&` +
      `time_increment=1`,
      {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      }
    );

    if (!insightsResponse.ok) {
      const error = await insightsResponse.json();
      handleMetaApiError(error);
    }

    const insights = await insightsResponse.json();
    console.log('Received insights response:', {
      dataCount: insights.data?.length,
      hasData: !!insights.data
    });

    const metricsData = insights.data?.length ? insights.data.map((insight: MetaAdsInsights, index: number) => {
      const date = new Date(today.getTime() - (insights.data.length - 1 - index) * 24 * 60 * 60 * 1000);
      if (!isValidDate(date)) {
        console.error('Invalid date generated:', date);
        return null;
      }

      return {
        user_id: session.user.id,
        platform_id: 'meta-ads',
        date: formatDateForAPI(date),
        metrics: {
          spend: parseFloat(insight.spend || '0'),
          impressions: parseInt(insight.impressions || '0'),
          clicks: parseInt(insight.clicks || '0'),
          ctr: parseFloat(insight.ctr || '0') * 100,
          reach: parseInt(insight.reach || '0'),
        },
      };
    }).filter(Boolean) : createZeroStateMetrics(session.user.id);

    await storeMetrics(metricsData);

    // Update connection with account info and last sync time
    const now = safeISOString(new Date());
    
    await supabase
      .from('platform_connections')
      .update({
        credentials: {
          access_token: accessToken,
          account_id: activeAccount.account_id,
          account_name: activeAccount.name,
          business_name: activeAccount.business_name,
          account_status: activeAccount.account_status,
          currency: activeAccount.currency,
          timezone: activeAccount.timezone_name,
          available_accounts: adAccounts.map(account => ({
            account_id: account.account_id,
            name: account.name,
            business_name: account.business_name,
            status: account.account_status,
            currency: account.currency,
            timezone: account.timezone_name
          })),
          last_sync: now
        },
        last_sync: now
      })
      .eq('user_id', session.user.id)
      .eq('platform_id', 'meta-ads');

    return {
      success: true,
      accountId: activeAccount.account_id,
      metrics: metricsData,
      accounts: adAccounts
    };
  } catch (error) {
    console.error('Meta Ads data fetch error:', error);
    handleMetaAdsError(error);
    throw error;
  }
}

async function fetchAdAccounts(accessToken: string): Promise<MetaAdAccount[]> {
  const accountResponse = await fetchWithRetry(
    'https://graph.facebook.com/v18.0/me/adaccounts?' +
    'fields=account_id,name,business_name,account_status,currency,timezone_name',
    {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    }
  );

  if (!accountResponse.ok) {
    const error = await accountResponse.json();
    handleMetaApiError(error);
  }

  const accountData = await accountResponse.json();
  
  if (!accountData.data?.length) {
    throw new Error('No Meta Ads accounts found. Please make sure you have access to an Ads account.');
  }

  return accountData.data.map((account: any) => ({
    id: account.id,
    account_id: account.account_id,
    name: account.name,
    business_name: account.business_name,
    account_status: account.account_status,
    currency: account.currency,
    timezone_name: account.timezone_name
  }));
}

async function fetchWithRetry(url: string, options: RequestInit, retries = 3): Promise<Response> {
  for (let i = 0; i < retries; i++) {
    try {
      const response = await fetch(url, options);
      if (response.ok || i === retries - 1) {
        return response;
      }
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    } catch (error) {
      if (i === retries - 1) throw error;
    }
  }
  throw new Error('Failed to fetch after multiple retries');
}

function handleMetaApiError(error: any) {
  if (error.error) {
    switch (error.error.code) {
      case 190:
        throw new Error('Session expired. Please reconnect your Meta Ads account.');
      case 200:
        throw new Error('No access to ad accounts. Please make sure you have granted the ads_read permission.');
      case 10:
        throw new Error('API rate limit reached. Please wait a few minutes and try again.');
      case 100:
        throw new Error('Invalid permissions. Please reconnect your account with the required permissions.');
      default:
        throw new Error(error.error.message || 'Failed to access Meta Ads data');
    }
  }
  throw new Error('Unknown error occurred while accessing Meta Ads data');
}

function createZeroStateMetrics(userId: string) {
  const metrics = [];
  const today = new Date();
  
  for (let i = 0; i < 30; i++) {
    const date = new Date(today.getTime() - i * 24 * 60 * 60 * 1000);
    if (!isValidDate(date)) {
      console.error('Invalid date in zero state metrics:', date);
      continue;
    }
    
    metrics.push({
      user_id: userId,
      platform_id: 'meta-ads',
      date: formatDateForAPI(date),
      metrics: {
        spend: 0,
        impressions: 0,
        clicks: 0,
        ctr: 0,
        reach: 0,
      },
    });
  }
  
  return metrics;
}

async function storeMetrics(metricsData: any[]) {
  const { error: metricsError } = await supabase
    .from('platform_metrics')
    .upsert(
      metricsData,
      { 
        onConflict: 'user_id,platform_id,date',
        ignoreDuplicates: false
      }
    );

  if (metricsError) throw metricsError;
}

function handleMetaAdsError(error: any) {
  let message = 'Failed to fetch Meta Ads data';

  if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'object' && error !== null) {
    const metaError = error as MetaAdsError;
    if (metaError.error) {
      switch (metaError.error.code) {
        case 190:
          message = 'Meta Ads session expired. Please reconnect your account.';
          break;
        case 100:
          message = 'Permission error. Please ensure you have granted the ads_read permission.';
          break;
        case 200:
          message = 'Insufficient permissions. Please check your Meta Ads access settings.';
          break;
        case 4:
          message = 'Meta Ads API rate limit reached. Please try again later.';
          break;
        default:
          message = metaError.error.message || 'Unknown error occurred';
      }
    }
  }

  toast.error(message, {
    duration: 5000,
    style: {
      maxWidth: '500px'
    }
  });
}